import '@/styles/style.css';

import { setDefaultOptions } from 'date-fns';
import { uk } from 'date-fns/locale';
import ui from '@nuxt/ui/vue-plugin';
import { createPinia } from 'pinia';
import type { Plugin } from 'vue';
import { createApp } from 'vue';

import { setupValibotI18n } from '@ugi/core';

import App from '@/App.vue';
import { apolloClient } from '@/core/apollo/client';
import router from '@/core/router';
import { useTheme } from '@/shared/composables/useTheme';

// Ukrainian-only product: make every date-fns call (format, formatDistanceToNow,
// …) default to the Ukrainian locale unless a call passes its own.
setDefaultOptions({ locale: uk });

setupValibotI18n();

// Тема застосовується до монтування: один спільний екземпляр `useColorMode`
// ставить клас на <html> і далі стежить за сховищем та налаштуванням ОС.
useTheme();

/**
 * Прибирає сліди закріпленої навігації з браузерів, які її встигли зберегти.
 *
 * Ключів два, і другий — не наш: `dashboard-sidebar-main` пише сам Nuxt UI
 * (`useResizable` збирає імʼя з `id` сайдбара) і тримає там `{size, collapsed}`.
 * Саме збережений `size` малював контейнер на 12rem замість рельси у 48px.
 *
 * Сайдбар більше не вмикає ні `collapsible`, ні `resizable`, тож сховище не
 * читається взагалі й ці ключі вже ні на що не впливають. Видаляємо, щоб вони
 * не ожили, якщо колись хтось поверне ці прапорці.
 */
for (const key of ['main-sidebar-collapsed', 'dashboard-sidebar-main']) {
  try {
    localStorage.removeItem(key);
  } catch {
    // Приватний режим або заблоковані site data — тоді там і зберігати не було чого.
  }
}

import * as plugins from '@/core/plugins';
import * as ApolloComposable from '@vue/apollo-composable';

import { registerModules } from '@/core/register-modules.ts';

import { AuthModule } from '@/modules/auth';
import { AcademicCalendarModule } from './modules/academic-calendar';
import { AuditLogModule } from './modules/audit-log';
import { ChatsModule } from './modules/chats';
import { DepartmentsModule } from './modules/departments';
import { DocumentsModule } from './modules/documents';
import { EnrollmentsModule } from './modules/enrollments';
import { GroupsModule } from './modules/groups';
import { HelpModule } from './modules/help';
import { InboxModule } from './modules/inbox';
import { IssuesModule } from './modules/issues';
import { KnowledgebaseModule } from './modules/knowledgebase';
import { McpModule } from './modules/mcp';
import { OrdersModule } from './modules/orders';
import { ProgramsModule } from './modules/programs';
import { SandboxModule } from './modules/sandbox';
import { ScheduleModule } from './modules/schedule';
import { StudentsModule } from './modules/students';
import { TeachersModule } from './modules/teachers';
import { UsersModule } from './modules/users';

registerModules({
  AuthModule,
  AcademicCalendarModule,
  AuditLogModule,
  ChatsModule,
  DepartmentsModule,
  DocumentsModule,
  EnrollmentsModule,
  GroupsModule,
  HelpModule,
  InboxModule,
  IssuesModule,
  KnowledgebaseModule,
  McpModule,
  OrdersModule,
  ProgramsModule,
  SandboxModule,
  ScheduleModule,
  StudentsModule,
  TeachersModule,
  UsersModule,
});

const app = createApp(App);

app.use(createPinia());
app.use(router);
app.use(ui);
app.provide(ApolloComposable.ApolloClients, {
  default: apolloClient,
});

for (const plugin of Object.values(plugins) as Plugin[]) {
  app.use(plugin);
}

router.isReady().then(() => {
  app.mount('#app');

  // Застосунок піднявся — знімаємо прапорець одноразового перезавантаження
  // зі `index.html`, щоб наступний зіпсований деплой у цій же вкладці теж мав
  // право на свою одну спробу.
  try {
    window.sessionStorage.removeItem('ugi:stale-bundle-reloaded');
  } catch {
    // sessionStorage може бути недоступний — тоді прапорця й не було.
  }
});
